import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Calendar,
  Clock,
  Users,
  FileText,
  ChevronRight,
  CheckCircle,
  XCircle,
  AlertCircle,
  Gift,
} from "lucide-react";
import { format } from "date-fns";

interface TimeOffMetrics {
  pendingRequests: number;
  employeesOutToday: number;
  upcomingTimeOff: number;
  activePolicies: number;
}

interface TimeOffRequest {
  request: {
    id: number;
    employeeId: number;
    policyId: number;
    startDate: string;
    endDate: string;
    totalHours: string;
    status: string;
    reason?: string;
    requestedAt: string;
  };
  policy: {
    id: number;
    name: string;
    color?: string;
  } | null;
  employee: {
    id: number;
    firstName: string;
    lastName: string;
  } | null;
}

export default function TimeOffOverview() {
  const { data: metrics, isLoading: metricsLoading } = useQuery<TimeOffMetrics>({
    queryKey: ["/api/time-off/metrics"],
  });

  const { data: pendingRequests, isLoading: pendingLoading } = useQuery<TimeOffRequest[]>({
    queryKey: ["/api/time-off/requests/pending"],
  });

  const { data: policies, isLoading: policiesLoading } = useQuery<any[]>({
    queryKey: ["/api/time-off/policies", { active: "true" }],
  });

  const formatDateRange = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (start === end) {
      return format(startDate, "MMM d, yyyy");
    }
    return `${format(startDate, "MMM d")} - ${format(endDate, "MMM d, yyyy")}`;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Time Off</h1>
          <p className="text-gray-500">Manage employee time off requests and policies</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Pending Requests
            </CardTitle>
            <AlertCircle className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {metricsLoading ? "..." : metrics?.pendingRequests || 0}
            </div>
            <p className="text-xs text-gray-500">Awaiting approval</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Out Today
            </CardTitle>
            <Users className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {metricsLoading ? "..." : metrics?.employeesOutToday || 0}
            </div>
            <p className="text-xs text-gray-500">Employees on time off</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Upcoming (7 days)
            </CardTitle>
            <Calendar className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {metricsLoading ? "..." : metrics?.upcomingTimeOff || 0}
            </div>
            <p className="text-xs text-gray-500">Approved time off</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Active Policies
            </CardTitle>
            <FileText className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {metricsLoading ? "..." : metrics?.activePolicies || 0}
            </div>
            <p className="text-xs text-gray-500">Time off types</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Pending Approvals</CardTitle>
            <Link href="/time-off/requests?status=pending">
              <Button variant="ghost" size="sm">
                View All <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {pendingLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
              </div>
            ) : !pendingRequests || pendingRequests.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-2" />
                <p>No pending requests</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingRequests.slice(0, 5).map((req) => (
                  <div
                    key={req.request.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: req.policy?.color || "#6B7280" }}
                      />
                      <div>
                        <p className="font-medium text-sm">
                          {req.employee?.firstName} {req.employee?.lastName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {req.policy?.name} - {formatDateRange(req.request.startDate, req.request.endDate)}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800">
                        {req.request.totalHours}h
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Time Off Policies</CardTitle>
            <Link href="/time-off/policies">
              <Button variant="ghost" size="sm">
                Manage <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {policiesLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
              </div>
            ) : !policies || policies.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                <p>No policies configured</p>
                <Link href="/time-off/policies">
                  <Button variant="link" className="mt-2">
                    Create your first policy
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {policies.slice(0, 5).map((policy) => (
                  <div
                    key={policy.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: policy.color || "#6B7280" }}
                      />
                      <div>
                        <p className="font-medium text-sm">{policy.name}</p>
                        <p className="text-xs text-gray-500">
                          {policy.accrualType === "none"
                            ? "No accrual"
                            : `${policy.accrualRate} ${policy.accrualUnit}/${policy.accrualType}`}
                        </p>
                      </div>
                    </div>
                    <Badge variant={policy.isActive ? "default" : "secondary"}>
                      {policy.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Link href="/time-off/requests">
          <Card className="h-full hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="flex items-center gap-4 p-6">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium">View Requests</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Review and manage time off requests</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </CardContent>
          </Card>
        </Link>

        <Link href="/time-off/calendar">
          <Card className="h-full hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="flex items-center gap-4 p-6">
              <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                <Users className="h-6 w-6 text-indigo-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium">Team Calendar</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">See who from your team is out and when</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </CardContent>
          </Card>
        </Link>

        <Link href="/time-off/balances">
          <Card className="h-full hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="flex items-center gap-4 p-6">
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Clock className="h-6 w-6 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium">View Balances</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Check employee time off balances</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </CardContent>
          </Card>
        </Link>

        <Link href="/time-off/holidays">
          <Card className="h-full hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="flex items-center gap-4 p-6">
              <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <Gift className="h-6 w-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium">Holidays</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Manage company-wide holidays</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </CardContent>
          </Card>
        </Link>

        <Link href="/time-off/settings">
          <Card className="h-full hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="flex items-center gap-4 p-6">
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <FileText className="h-6 w-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium">Settings</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Configure time off module settings</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
}
